<template>
  <div>
    <!-- MODO “TELA NORMAL”: seleção de período + botão -->
    <div v-if="!printMode">
      <h6>Extrato de Matrículas e Mensalidades Recebidas</h6><p></p>
      <div class="row mb-3">
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicioLocal" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFimLocal" class="form-control" />
        </div>
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO “IMPRESSÃO/PRINT”: exibe duas tabelas (Matrícula e Mensalidade) -->
    <div v-else>
      <p class="text-center" style="font-size: 14px;">
        ( {{ globalUtils.formatarData(dataInicio) }} a {{ globalUtils.formatarData(dataFim) }} )
      </p>

      <div v-if="loading">Carregando…</div>
      <div v-else>
        <!-- Tabela de Matrículas -->
        <div v-if="matriculas.length">
        <h6>Matrículas</h6>
          <table class="table table-striped mb-4">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Pagamento</th>
                <th>Prev. Receb.</th>
                <th class="text-right">Valor</th>
                <th>Descrição</th>
                <th>Forma de Pagamento</th>
                <th>Autorização</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in matriculas" :key="'mat-' + idx">
                <td>{{ globalUtils.formatarData(item.vencimento) }}</td>
                <td>{{ globalUtils.formatarData(item.pagamento) }}</td>
                <td>{{ globalUtils.formatarData(item.recebimento) }}</td>
                <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
                <td>{{ item.descricao }}</td>
                <td>{{ item.forma_pagamento }}</td>
                <td>{{ item.numero_autorizacao || '—' }}</td>
              </tr>
            </tbody>
            <tfoot v-if="matriculas.length">
              <tr>
                <td colspan="2"><strong>Total Matrículas:</strong></td>
                <td class="text-right">
                  <strong>{{ formatarMoeda(totalMatriculas) }}</strong>
                </td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <p class="text-muted">Nenhuma matrícula encontrada neste período.</p>
        </div>

        <!-- Tabela de Mensalidades -->
        <div v-if="mensalidades.length">
        <h6>Mensalidades</h6>
          <table class="table table-striped mb-2">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Pagamento</th>
                <th>Prev. Receb.</th>
                <th class="text-right">Valor</th>
                <th>Descrição</th>
                <th>Forma de Pagamento</th>
                <th>Autorização</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in mensalidades" :key="'men-' + idx">
                <td>{{ globalUtils.formatarData(item.vencimento) }}</td>
                <td>{{ globalUtils.formatarData(item.pagamento) }}</td>
                <td>{{ globalUtils.formatarData(item.recebimento) }}</td>
                <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
                <td>{{ item.descricao }}</td>
                <td>{{ item.forma_pagamento }}</td>
                <td>{{ item.numero_autorizacao || '—' }}</td>
              </tr>
            </tbody>
            <tfoot v-if="mensalidades.length">
              <tr>
                <td colspan="2"><strong>Total Mensalidades:</strong></td>
                <td class="text-right">
                  <strong>{{ formatarMoeda(totalMensalidades) }}</strong>
                </td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <p class="text-muted">Nenhuma mensalidade encontrada neste período.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  printTitle: { type: String, default: 'Relatório' },
  subtitle:   { type: String, default: '' }
})

const router = useRouter()

// Estados locais (modo “tela normal”)
const dataInicioLocal = ref('')
const dataFimLocal    = ref('')

// Estados de resultado (modo “print”)
const loading       = ref(false)
const matriculas    = ref([])
const mensalidades  = ref([])

// Totais somados em JS
 const totalMatriculas = computed(() => {
   return matriculas.value.reduce((acc, row) => {
     // parseFloat(row.valor) converte a string em número; se falhar, usa 0
     const v = parseFloat(row.valor) || 0
     return acc + v
   }, 0)
 })
 const totalMensalidades = computed(() => {
   return mensalidades.value.reduce((acc, row) => {
     const v = parseFloat(row.valor) || 0
     return acc + v
   }, 0)
 })

// Se estivermos em printMode, buscar os dados no onMounted()
onMounted(async () => {
  if (!props.printMode) return
  if (!props.dataInicio || !props.dataFim) return

  loading.value = true
  try {
    const res = await axios.get(
      '/gestao/relatorios/financeiros/extrato-mensalidades-matriculas',
      {
        params: {
          dataInicio: props.dataInicio,
          dataFim:    props.dataFim
        }
      }
    )
    // O JSON vem com { matriculas: [...], mensalidades: [...] }
    matriculas.value   = Array.isArray(res.data.matriculas)   ? res.data.matriculas   : []
    mensalidades.value = Array.isArray(res.data.mensalidades) ? res.data.mensalidades : []
  } catch (e) {
    console.error('Erro ao buscar extrato de mensalidades/matrículas:', e)
    matriculas.value   = []
    mensalidades.value = []
  } finally {
    loading.value = false
  }
})

// Função chamada ao clicar em “Executar” (modo normal)
function abrirPrint() {
  if (!dataInicioLocal.value || !dataFimLocal.value) return

  const inicioFormatado = globalUtils.formatarData(dataInicioLocal.value)
  const fimFormatado    = globalUtils.formatarData(dataFimLocal.value)
  const subtitle        = `( ${inicioFormatado} a ${fimFormatado} )`

  router.push({
    path: `/gestao/relatorios/print/extrato-mensalidades-matriculas`,
    query: {
      title:      'Extrato de Matrículas e Mensalidades Recebidas',
      dataInicio: dataInicioLocal.value,
      dataFim:    dataFimLocal.value,
      subtitle:   subtitle
    }
  })
}

// Formata valor numérico para moeda brasileira
function formatarMoeda(num) {
  if (num == null) return ''
  return Number(num).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.text-right {
  text-align: right !important;
}
</style>
